import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

/**
 * Check if current user is connected with another user
 * Uses the are_users_connected() database function (unified connections table)
 */
export function useAreUsersConnected(otherUserId: string | null | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["are_users_connected", user?.id, otherUserId],
    queryFn: async () => {
      if (!user?.id || !otherUserId || user.id === otherUserId) {
        return false;
      }

      const { data, error } = await supabase.rpc("are_users_connected", {
        user1_id: user.id,
        user2_id: otherUserId,
      });

      if (error) {
        console.error("Error checking connection status:", error);
        return false;
      }

      return data as boolean;
    },
    enabled: !!user?.id && !!otherUserId && user.id !== otherUserId,
    staleTime: 30 * 1000,
    gcTime: 60 * 1000,
  });
}

/**
 * Get the connection status between current user and another user
 * Uses the get_connection_status() database function (unified connections table)
 * Returns: 'pending', 'accepted', 'declined', 'blocked', or 'none'
 */
export function useConnectionStatus(otherUserId: string | null | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["connection_status", user?.id, otherUserId],
    queryFn: async () => {
      if (!user?.id || !otherUserId || user.id === otherUserId) {
        return null;
      }

      const { data, error } = await supabase.rpc("get_connection_status", {
        user1_id: user.id,
        user2_id: otherUserId,
      });

      if (error) {
        console.error("Error getting connection status:", error);
        return null;
      }

      return data as string | null;
    },
    enabled: !!user?.id && !!otherUserId && user.id !== otherUserId,
    staleTime: 30 * 1000,
    gcTime: 60 * 1000,
  });
}

/**
 * Get the connection type between current user and another user
 * Uses the get_connection_type() database function (unified connections table)
 * Returns: 'community', 'mentorship', or 'none' (only for accepted connections)
 */
export function useConnectionType(otherUserId: string | null | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["connection_type", user?.id, otherUserId],
    queryFn: async () => {
      if (!user?.id || !otherUserId || user.id === otherUserId) {
        return 'none';
      }

      const { data, error } = await supabase.rpc("get_connection_type", {
        user1_id: user.id,
        user2_id: otherUserId,
      });

      if (error) {
        console.error("Error getting connection type:", error);
        return 'none';
      }

      return (data as string) || 'none';
    },
    enabled: !!user?.id && !!otherUserId && user.id !== otherUserId,
    staleTime: 30 * 1000,
    gcTime: 60 * 1000,
  });
}

/**
 * Check if users have an active mentorship relationship
 * Uses the has_active_mentorship() database function (unified connections table)
 */
export function useHasActiveMentorship(otherUserId: string | null | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["has_active_mentorship", user?.id, otherUserId],
    queryFn: async () => {
      if (!user?.id || !otherUserId || user.id === otherUserId) {
        return false;
      }

      const { data, error } = await supabase.rpc("has_active_mentorship", {
        user1_id: user.id,
        user2_id: otherUserId,
      });

      if (error) {
        console.error("Error checking active mentorship:", error);
        return false;
      }

      return data as boolean;
    },
    enabled: !!user?.id && !!otherUserId && user.id !== otherUserId,
    staleTime: 30 * 1000,
    gcTime: 60 * 1000,
  });
}

/**
 * Get full connection details between current user and another user
 * Uses the get_connection_details() database function (unified connections table)
 */
export function useConnectionDetails(otherUserId: string | null | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["connection_details", user?.id, otherUserId],
    queryFn: async () => {
      if (!user?.id || !otherUserId || user.id === otherUserId) {
        return null;
      }

      const { data, error } = await supabase.rpc("get_connection_details", {
        user1_id: user.id,
        user2_id: otherUserId,
      });

      if (error) {
        console.error("Error getting connection details:", error);
        return null;
      }

      // The RPC returns an array, get the first result
      const result = Array.isArray(data) ? data[0] : data;
      if (!result) return null;

      return result as {
        connection_id: string;
        status: string;
        connection_type: string;
        is_mentorship_active: boolean;
        scheduled_at: string | null;
        zoom_link: string | null;
        accepted_at: string | null;
        created_at: string;
        thread_id: string | null;
      };
    },
    enabled: !!user?.id && !!otherUserId && user.id !== otherUserId,
    staleTime: 30 * 1000,
    gcTime: 60 * 1000,
  });
}

/**
 * Check if viewer can see private profile fields using the database function
 */
export function useCanViewPrivateProfile(profileId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["can_view_private_profile", user?.id, profileId],
    queryFn: async () => {
      if (!user?.id || !profileId) return false;

      // Own profile - always can view
      if (user.id === profileId) return true;

      const { data, error } = await supabase.rpc("can_view_private_profile", {
        _profile_id: profileId,
        _viewer_id: user.id,
      });

      if (error) {
        console.error("Error checking privacy permissions:", error);
        return false;
      }
      return data as boolean;
    },
    enabled: !!user?.id && !!profileId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Fetch a profile with privacy-aware field masking
 */
export function useProfileWithPrivacy(profileId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["profile_with_privacy", user?.id, profileId],
    queryFn: async () => {
      if (!user?.id || !profileId) return null;

      const { data, error } = await supabase.rpc("get_profile_with_privacy", {
        _profile_id: profileId,
        _viewer_id: user.id,
      });

      if (error) {
        console.error("Error fetching profile with privacy:", error);
        throw error;
      }
      
      // The function returns an array, get the first (and only) result
      return data?.[0] || null;
    },
    enabled: !!user?.id && !!profileId,
    staleTime: 2 * 60 * 1000,
  });
}
